<template>
  <section
    class="invoice-preview-wrapper delivery-note-show-page"
    style="position: relative"
  >
    <!-- Alerte : Aucun bon trouvé -->
    <!-- Loading spinner -->
    <div
      v-if="orderLoading"
      class="text-center py-5"
    >
      <b-spinner
        variant="primary"
        label="Chargement..."
      />
      <p class="mt-2">
        Chargement du bon de livraison...
      </p>
    </div>

    <b-alert
      variant="danger"
      :show="orderError"
      aria-live="assertive"
      aria-atomic="true"
    >
      <h4 class="alert-heading">
        Erreur lors de la récupération du bon de livraison
      </h4>
      <div class="alert-body">
        Aucun bon de livraison trouvé avec cet identifiant. Consultez
        <b-link
          class="alert-link"
          :to="{ name: 'delivery-note-list' }"
        >
          la liste des bons de livraison
        </b-link>
        pour d'autres bons.
      </div>
    </b-alert>

    <b-row
      v-if="order"
      class="invoice-preview"
    >
      <!-- Col: Left (Invoice Container) -->
      <b-col
        cols="12"
        xl="9"
        md="8"
      >
        <b-card
          v-if="order"
          no-body
          class="invoice-preview-card pagebreak"
        >

          <div
            class="d-flex justify-content-between flex-wrap mr-1 ml-1"
          >

            <div class="invoice-preview">
              <h1
                class="mt-1"
                style="color: #0A2634; font-size: 30px"
                aria-label="En-tête du bon de livraison"
              >
                Bon de livraison
                <span
                  class="invoice-title invoice-number"
                  style="color: #0A2634;"
                >
                  N° : BL-{{ order.id }}
                </span>
              </h1>
              <h3 style="color: black ">
                Date :
                <span
                  class="invoice-date"
                  style="color: black"
                >
                  {{ formatFn(order.created_at) }}</span>
              </h3>
              <div class="mt-1">
                <div class="mt-1">
                  <b-card-text
                    class="h4"
                    style="color: black"
                  >
                    <strong style="color:#0A2634">{{ company.name }} :</strong> {{ company.tagline || 'Société de logistique et livraison de colis' }}
                  </b-card-text>
                  <b-card-text
                    class="h4"
                    style="color: black"
                  >
                    <strong style="color:#0A2634">Matricule fiscale :</strong> {{ company.tax_identification_number ? '***' + company.tax_identification_number.slice(3) : '' }}
                  </b-card-text>
                  <b-card-text
                    class="h4"
                    style="color: black"
                  >
                    <strong style="color:#0A2634">Adresse :</strong> {{ company.address }}
                  </b-card-text>
                  <b-card-text
                    class="h4"
                    style="color: black"
                  >
                    <strong style="color:#0A2634">Téléphone :</strong> {{ company.phone1 }}
                  </b-card-text>
                </div>
              </div>
            </div>

            <div style="text-align:end">
              <div>
                <img
                  src="/logo.png"
                  width="180px"
                  height="auto"
                  alt="Logo de la société de livraison"
                  class="logo-top"
                >
              </div>
              <div class="d-flex justify-content-end align-items-start mt-0 codes-right">
                <div class="header-qr-wrap">
                  <qrcode-vue
                    :value="headerBarcodeValue(order)"
                    :size="headerQrSize"
                    level="H"
                  />
                  <span aria-label="Identifiant code-barres">{{ headerBarcodeValue(order) }}</span>
                </div>
                <div class="header-barcode-wrap">
                  <barcode
                    :value="headerBarcodeValue(order)"
                    :height="78"
                    :width="2.1"
                    :font-size="28"
                    font-options="bold"
                    :text-margin="2"
                    :margin="0"
                    :display-value="true"
                    class="barcode-small"
                    aria-label="Code-barres de la commande"
                  />
                </div>
              </div>
            </div>
          </div>
          <b-card-text
            v-if="isFlagEnabled(order.is_fragile)"
            class="status-banner status-banner--fragile"
          >
            <strong>Fragile</strong>
          </b-card-text>
          <div
            v-if="order.repository"
            class="dn-agence-line mr-1 ml-1 mb-1"
          >
            <strong>{{ order.repository.name }}</strong>
            &nbsp;&gt;&gt;------- dispatch -------&gt;&gt;&nbsp;
            <strong>{{ dispatchGovernorateName(order) }}</strong>
          </div>
          <div class="d-flex flex-row flex-wrap justify-content-between dn-info-row mr-1 ml-1 mb-1">

            <div class="dn-info-col dn-info-col--client">

              <div
                class="client-info-box"
                style="border: 2px solid black; font-size: 18px; min-width: 380px;"
              >
                <b-card-text class="ml-2 mr-2 mt-2 mb-1">
                  <span
                    style="color: #1565C0"
                  >Destinataire : </span> <strong style="font-weight: 900; color: #000;">{{ order.name }}</strong>
                </b-card-text>
                <b-card-text class="ml-2 mr-2 mb-1">
                  <span
                    style="color: #1565C0"
                  >Gouvernorat : </span><strong style="font-weight: 900; color: #000;">{{ typeof order.governorate === 'object' ? order.governorate.name : order.governorate }}
                    <span
                      v-if="order.delegation != null"
                      style="color:rgb(12, 5, 5)"
                    > , {{ typeof order.delegation === 'object' ? order.delegation.name : order.delegation }}</span>
                    <span v-if="order.locality"> , {{ typeof order.locality === 'object' ? order.locality.name : order.locality }}</span></strong>
                </b-card-text>

                <b-card-text class="ml-2 mr-2 mb-1">
                  <span style="color: #1565C0">Adresse : </span>
                  <strong style="font-weight: 900; color: #000;">{{ order.address }}</strong>
                </b-card-text>
                <b-card-text class="ml-2 mr-2 mb-1">
                  <span
                    style="color: #1565C0"
                  >Téléphone : </span>
                  <strong style="font-weight: 900; color: #000;">{{ order.phone1 }}<span v-if="order.phone2"> / {{ order.phone2 }}</span></strong>
                </b-card-text>
              </div>
            </div>
            <div class="dn-info-col dn-info-col--supplier">

              <div
                v-if="order.supplier"
                class="supplier-info-box"
                style="border: 2px solid black; font-size: 18px; min-width: 250px;"
              >

                <b-card-text class="ml-2 mr-2 mt-2 mb-1">
                  <span
                    style="color: #1565C0"
                  >Expéditeur : </span><strong style="font-weight: 900; color: #000;">{{ supplierBordereauValue('username', order.supplier.page_name || order.supplier.username) }}
                    <span
                      v-if="order.supplier.is_external"
                      class="badge badge-info ml-2"
                    >{{ order.supplier.source_company }}</span></strong>
                </b-card-text>
                <b-card-text class="ml-2 mr-2 mb-1">
                  <span
                    style="color: #1565C0"
                  >Téléphone : </span><strong style="font-weight: 900; color: #000;">{{ supplierBordereauValue('phone_number', supplierPhoneDisplay(order)) }}</strong>
                </b-card-text>
                <b-card-text class="ml-2 mr-2 mb-1">
                  <span
                    style="color: #1565C0"
                  >Adresse : </span>
                  <strong style="font-weight: 900; color: #000;">{{ supplierBordereauValue('address', order.supplier.address) }}</strong>
                </b-card-text>
                <b-card-text
                  v-if="order.supplier.external_barcode"
                  class="ml-2 mr-2 mb-1"
                >
                  <span style="color: #1565C0">Code-barres externe : </span>
                  <strong style="font-weight: 900; color: #000;">{{ order.supplier.external_barcode }}</strong>
                </b-card-text>
                <b-card-text
                  v-if="supplierIdentityField(order.supplier)"
                  class="ml-2 mr-2 mb-1"
                >
                  <span style="color: #1565C0">{{ supplierIdentityField(order.supplier).label }} : </span>
                  <strong style="font-weight: 900; color: #000;">{{ supplierIdentityField(order.supplier).value }}</strong>
                </b-card-text>
              </div>
            </div>

          </div>

          <!-- Invoice Description: Table -->
          <div class="mr-1 ml-1">
            <b-table-lite
              class="ta"
              style="font-size: 20px; border: 2px solid black"
              responsive
              :items="p"
              :fields="['désignation', 'quantité']"
            >
              <!-- Slot removed: taskDescription was not used -->
            </b-table-lite>
          </div>
          <!-- End table wrapper -->
          <!-- Invoice Description: Total -->
          <!-- Price + Commentaire on same row right after table -->
          <div class="mr-1 ml-1 dn-after-table-row">
            <div class="dn-after-table-left">
              <h1
                class="dn-comment-line"
                aria-label="Commentaire du bon de livraison"
              >
                Commentaire :
                <span class="dn-comment-text">{{ order.note }}</span>
              </h1>
              <div class="dn-flags-list">
                <b-card-text
                  v-if="isFlagEnabled(order.is_an_exchange)"
                  class="h4"
                  style="color:black"
                >
                  <feather-icon
                    icon="RefreshCwIcon"
                    size="18"
                    class="mr-50"
                    style="color: #5A8DEE;"
                  />
                  <strong style="color:#1565C0">Échange :</strong> OUI
                </b-card-text>
                <b-card-text
                  v-if="isFlagEnabled(order.is_fragile)"
                  class="h4"
                  style="color:black"
                >
                  <feather-icon
                    icon="AlertTriangleIcon"
                    size="18"
                    class="mr-50"
                    style="color: #FDAC41;"
                  />
                  <strong style="color:#1565C0">Fragile :</strong> OUI
                </b-card-text>
                <b-card-text
                  v-if="isFlagEnabled(order.is_discharge)"
                  class="h4"
                  style="color:black"
                >
                  <feather-icon
                    icon="FileTextIcon"
                    size="18"
                    class="mr-50"
                    style="color: #A3A1FB;"
                  />
                  <strong style="color:#1565C0">Décharge :</strong> OUI
                </b-card-text>
                <b-card-text
                  class="h4"
                  style="color:black"
                >
                  <feather-icon
                    icon="PackageIcon"
                    size="18"
                    class="mr-50"
                    style="color: #C59C6C;"
                  />
                  <strong style="color:#1565C0">ouvrir colis :</strong> {{ isFlagEnabled(order.can_open_package) ? 'OUI' : 'NON' }}
                </b-card-text>
              </div>
            </div>
            <div class="dn-after-table-right">
              <div class="dn-total-box">
                <table style="border: 2px solid #0A2634">
                  <tr class="price-row price-row-ttc">
                    <th class="text-center dn-price-ttc">
                      Total TTC: {{ order.price_ttc }} DT
                    </th>
                  </tr>
                </table>
              </div>
            </div>
          </div>

          <!-- QR code at bottom -->
          <div
            v-if="footerBarcodeValue(order)"
            class="mr-1 ml-1 dn-qr-section"
          >
            <div class="footer-codes-wrap">
              <div class="footer-qr-wrap">
                <qrcode-vue
                  :value="footerBarcodeValue(order)"
                  :size="size"
                  level="H"
                />
              </div>
              <div class="footer-barcode-wrap">
                <barcode
                  :value="footerBarcodeValue(order)"
                  :height="88"
                  :width="1.6"
                  :font-size="24"
                  font-options="bold"
                  :text-margin="3"
                  :margin="0"
                  :display-value="true"
                  class="footer-barcode"
                  aria-label="Code-barres externe du bon de livraison"
                />
              </div>
            </div>
          </div>

        </b-card>
      </b-col>

      <!-- Right Col: Card -->
      <b-col
        cols="12"
        md="4"
        xl="3"
        class="invoice-actions"
      >
        <b-card>
          <!-- Button: Print -->
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-secondary"
            class="mb-75"
            block
            @click="printInvoice"
          >
            Imprimer
          </b-button>

          <!-- Button: Edit -->
        </b-card>
      </b-col>
    </b-row>
  </section>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BTableLite,
  BCardText,
  BButton,
  BAlert,
  BLink,
  BSpinner,
  VBToggle,
} from 'bootstrap-vue'

import Ripple from 'vue-ripple-directive'
import axios from 'axios'
import storeAuth from '@/store/store'
import QrcodeVue from 'qrcode.vue'
import VueBarcode from 'vue-barcode'

export default {
  directives: {
    Ripple,
    'b-toggle': VBToggle,
  },
  components: {
    BRow,
    BCol,
    BCard,
    BTableLite,
    BCardText,
    BButton,
    BAlert,
    BLink,
    BSpinner,
    QrcodeVue,
    barcode: VueBarcode,
  },
  data() {
    return {
      size: 110,
      headerQrSize: 108,
      p: [],
      order: null,
      orderError: false,
      orderLoading: true,
      company: {},
    }
  },

  created() {
    this.id = this.$route.params.id
    this.company = storeAuth.state.company
    this.getOrder()
  },
  methods: {
    formatFn(date) {
      if (!date) {
        return null
      }
      const [year, month, day] = date.split('-')
      const day1 = day.substr(0, 2)
      const h = date.slice(11, 19)
      return `${year}-${month.padStart(2, '0')}-${day1.padStart(
        2,
        '0',
      )} | ${h}`
    },
    formatDate(date) {
      if (!date) {
        return null
      }
      const [year] = date.split('-')
      return `${year.slice(2, 4)}`
    },
    parseDate(date) {
      if (!date) {
        return null
      }
      const [month, day, year] = date.split('/')
      return `${year.padStart(0, '0')}-${month.padStart(2, '0')}-${day.padStart(
        2,
        '0',
      )}`
    },
    addnewitem(qte, nom) {
      this.p.push({
        désignation: nom,
        quantité: qte,
      })
    },
    locationName(value) {
      if (!value) {
        return ''
      }

      if (typeof value === 'object') {
        return value.name || ''
      }

      if (typeof value === 'string') {
        const trimmedValue = value.trim()

        if (!trimmedValue) {
          return ''
        }

        const matches = trimmedValue.match(/\{.*\}/)

        if (matches && matches[0]) {
          try {
            const parsedValue = JSON.parse(matches[0])

            if (parsedValue && parsedValue.name) {
              return parsedValue.name
            }
          } catch (error) {
            return trimmedValue
          }
        }

        return trimmedValue
      }

      return ''
    },
    dispatchGovernorateName(order) {
      const governorateName = this.locationName(order ? order.governorate : null)

      if (governorateName) {
        return governorateName
      }

      if (order && order.destination_repository_name) {
        return order.destination_repository_name
      }

      if (order && order.repository && order.repository.name) {
        return order.repository.name
      }

      return ''
    },
    supplierPhoneDisplay(order) {
      if (!order || !order.supplier) {
        return ''
      }

      return [order.supplier.phone, order.supplier.phone2]
        .filter(phone => phone !== undefined && phone !== null && String(phone).trim())
        .join(' / ')
    },
    maskedSupplierTaxId(order) {
      const taxId = order && order.supplier && order.supplier.tax_identification_number
      if (!taxId) {
        return ''
      }

      const value = String(taxId).trim()
      if (value.length <= 4) {
        return '****'
      }

      return `****${value.slice(4)}`
    },
    supplierPhoneValue(supplier) {
      if (!supplier) return ''
      return [supplier.phone, supplier.phone2].filter(Boolean).join(' / ')
    },
    supplierBordereauValue(field, fallback = '') {
      const display = this.order
        && this.order.supplier
        && this.order.supplier.bordereau_display
        && this.order.supplier.bordereau_display[field]

      if (display && Object.prototype.hasOwnProperty.call(display, 'display_value')) {
        return display.display_value
      }

      return fallback || ''
    },
    supplierBordereauDisplay(supplier, field) {
      return supplier
        && supplier.bordereau_display
        && supplier.bordereau_display[field]
    },
    supplierIdentityField(supplier) {
      if (!supplier) return null

      const fields = [
        {
          key: 'matricule_fiscale',
          label: 'Matricule fiscale',
          fallback: supplier.tax_identification_number,
        },
        {
          key: 'cin',
          label: 'CIN',
          fallback: supplier.cin,
        },
      ]

      const visibleField = fields.find(field => {
        const display = this.supplierBordereauDisplay(supplier, field.key)
        return display && display.show === true && (display.value || field.fallback)
      })

      const selectedField = visibleField || fields.find(field => {
        const display = this.supplierBordereauDisplay(supplier, field.key)
        return display && display.show === false
      }) || fields.find(field => field.fallback)

      if (!selectedField) return null

      return {
        label: selectedField.label,
        value: this.supplierBordereauValue(selectedField.key, selectedField.fallback),
      }
    },
    async getOrder() {
      try {
        const { data: orderData } = await axios.get(`/api/orders/detail/${this.$route.params.id}/`)

        // Process governorate data if needed
        if (orderData.governorate) {
          if (typeof orderData.governorate === 'object' && orderData.governorate.name) {
            // The governorate data is already properly structured as an object with a name property
            // No need to modify it as we directly access order.governorate.name in the template
          } else if (typeof orderData.governorate === 'string' && orderData.governorate.includes('{')) {
            try {
              const matches = orderData.governorate.match(/\{.*\}/)
              if (matches && matches[0]) {
                const governorateObj = JSON.parse(matches[0])
                orderData.governorate = governorateObj
              }
            } catch (e) {
              // error parsing, ignore
            }
          }
        }

        // Same process for delegation if needed
        if (orderData.delegation) {
          if (typeof orderData.delegation === 'object' && orderData.delegation.name) {
            // The delegation data is already properly structured
            // No need to modify it
          } else if (typeof orderData.delegation === 'string' && orderData.delegation.includes('{')) {
            try {
              const matches = orderData.delegation.match(/\{.*\}/)
              if (matches && matches[0]) {
                const delegationObj = JSON.parse(matches[0])
                orderData.delegation = delegationObj
              }
            } catch (e) {
              // error parsing, ignore
            }
          }
        }

        // Same process for locality if needed
        if (orderData.locality) {
          if (typeof orderData.locality === 'object' && orderData.locality.name) {
            // The locality data is already properly structured
            // No need to modify it
          } else if (typeof orderData.locality === 'string' && orderData.locality.includes('{')) {
            try {
              const matches = orderData.locality.match(/\{.*\}/)
              if (matches && matches[0]) {
                const localityObj = JSON.parse(matches[0])
                orderData.locality = localityObj
              }
            } catch (e) {
              // error parsing, ignore
            }
          }
        }

        this.order = orderData
        if (this.order != null && this.order.products) {
          for (let i = 0; i < this.order.products.length; i += 1) {
            this.addnewitem(this.order.products[i].quantity, this.order.products[i].product)
          }
        }
      } catch (e) {
        this.order = null
        this.orderError = true
      } finally {
        this.orderLoading = false
      }
    },
    isFlagEnabled(value) {
      if (typeof value === 'boolean') return value
      if (typeof value === 'number') return value === 1
      if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase()
        return normalized === '1' || normalized === 'true' || normalized === 'yes' || normalized === 'oui'
      }
      return false
    },
    normalizedBarcodeValue(value) {
      return String(value === undefined || value === null ? '' : value).trim()
    },
    deliveryNoteBarcodeMode(order) {
      const displayMode = order && order.delivery_note_barcode && order.delivery_note_barcode.mode
      if (displayMode) {
        return displayMode
      }

      const companyMode = (this.company && this.company.delivery_note_barcode_mode) || 'order_id_and_external_id'
      const isSyncRelatedOrder = Boolean(
        this.normalizedBarcodeValue(order && order.external_id)
          || (order && (order.is_outsourcing || order.outsourcing_company)),
      )
      return companyMode === 'external_id_only' && isSyncRelatedOrder
        ? companyMode
        : 'order_id_and_external_id'
    },
    headerBarcodeValue(order) {
      const display = order && order.delivery_note_barcode
      if (display && display.primary_value) {
        return this.normalizedBarcodeValue(display.primary_value)
      }

      const externalId = this.normalizedBarcodeValue(order && order.external_id)
      const orderId = this.normalizedBarcodeValue(order && order.id)
      if (this.deliveryNoteBarcodeMode(order) === 'external_id_only') {
        return externalId || orderId
      }

      return orderId
    },
    footerBarcodeValue(order) {
      const display = order && order.delivery_note_barcode
      if (display && display.secondary_value) {
        return this.normalizedBarcodeValue(display.secondary_value)
      }

      if (this.deliveryNoteBarcodeMode(order) === 'external_id_only') {
        return ''
      }

      return this.normalizedBarcodeValue(order && order.external_id)
    },
    bottomCodeValue(order) {
      return this.footerBarcodeValue(order)
    },
    printInvoice() {
      window.print()
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~@core/scss/base/pages/app-invoice.scss";

// JG Brand Colors
$jg-primary: #1565C0;
$jg-accent: #f28d44;
$jg-light: #fff5ed;

table,
td,
th {
  padding: 10px;
  text-align: left;
  border: 1px solid #1565C0;
}

table {
  width: 100%;
  border-collapse: collapse;
}

// ── Header right codes ──
.dn-header-right-codes {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.dn-header-codes {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-top: 6px;
}

// ── Responsive delivery note layout ──
.dn-info-row {
  gap: 8px;
}

.dn-info-col {
  flex: 1 1 300px;
  min-width: 0;
}

.dn-info-col--client .client-info-box {
  font-size: 18px;
}

.dn-info-col--supplier .supplier-info-box {
  font-size: 18px;
}

// On narrow screens (mobile / iOS) stack the boxes vertically
@media (max-width: 768px) {
  .footer-codes-wrap {
    align-items: flex-start;
  }

  .dn-info-col {
    flex: 1 1 100%;
  }

  .dn-info-col--client .client-info-box {
    font-size: 16px;
  }

  .dn-info-col--supplier .supplier-info-box {
    font-size: 12px;
    margin-left: 0 !important;
  }

  .invoice-preview h1 {
    font-size: 22px !important;
  }

  .invoice-preview h3 {
    font-size: 16px !important;
  }

  .dn-header-logo {
    width: 140px !important;
  }

  .company-meta .h4 {
    font-size: 12px !important;
  }

  .ta {
    font-size: 14px !important;
  }

  .dn-total-section h1 {
    font-size: 14px !important;
  }
}

// Header right codes tweaks
.codes-right {
  margin-top: -12px; // lift a bit more to align with company info
  gap: 0;
}

.codes-right span {
  font-size: 18px;
  font-weight: 800;
  font-family: monospace;
  line-height: 1;
}

.header-qr-wrap,
.header-barcode-wrap {
  flex: 0 0 auto;
}

.header-qr-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.header-barcode-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-left: -4px;
}

.barcode-small {
  display: block;
  overflow: visible;

  svg {
    display: block !important;
    width: 390px !important;
    max-width: 390px !important;
    height: 132px !important;
    overflow: visible !important;
  }
}

.barcode-text-fallback {
  font-size: 14px;
  font-family: monospace;
  letter-spacing: 2px;
  text-align: center;
  margin-top: 2px;
  color: #000;
}

.logo-top {
  margin-top: -10px; // push logo up a bit
}

.codes-left {
  gap: 0;
}

.barcode-holder {
  margin-top: 4px;
  margin-bottom: 10px;
}

// Price table rows with JG brand colors
.price-row {
  th {
    padding: 12px 20px;
    font-weight: 600;
    color: #1565C0;
  }

  &.price-row-ht {
    background-color: #ffd8bd;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  &.price-row-tva {
    background-color: #fff5ed;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  &.price-row-ttc {
    background-color: #fff;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;

    th {
      font-size: 1.1em;
      font-weight: 700;
      color: #0A2634;
    }
  }
}

.dn-price-ttc {
  font-size: 28px !important;
  font-weight: 900 !important;
  letter-spacing: 0.5px;
}

.mycolor {
  background-color: #1565C0;
}
.contact {
  background-color: #1565C0;
  padding: 10px;
  border-radius: 20px;
}

.dn-after-table-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
  margin: 4px 0 0;
  padding: 0;
  flex: 0 0 auto;
}

.dn-after-table-left {
  flex: 1 1 auto;
  min-width: 0;
}

.dn-after-table-right {
  flex: 0 0 auto;
  align-self: flex-start;
}

.dn-qr-section {
  margin-top: auto;
  padding-bottom: 6px;
  flex: 0 0 auto;
}

.footer-codes-wrap {
  display: inline-flex;
  align-items: flex-end;
  gap: 0;
  flex-wrap: nowrap;
  page-break-inside: avoid;
  break-inside: avoid;
}

.footer-qr-wrap {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  flex: 0 0 auto;
}

.footer-barcode-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  margin-left: 4px;
  flex: 0 0 auto;
}

.footer-barcode {
  display: block;
  width: 260px !important;
  max-width: 260px !important;
  height: 110px !important;
  overflow: visible;

  svg {
    display: block !important;
    width: 260px !important;
    max-width: 260px !important;
    height: 110px !important;
    overflow: visible !important;
  }
}

.dn-agence-line {
  font-size: 16px;
  font-weight: bold;
  text-align: center;
  padding: 4px 8px;
  border: 1px solid #0A2634;
  color: #0A2634;
  margin-bottom: 6px;
}

.dn-comment-line {
  margin: 0;
  color: #1565C0;
  font-size: 22px;
  font-weight: bold;
  line-height: 1.2;
}

.dn-comment-text {
  color: #000;
  font-size: 18px;
  font-weight: normal;
}

.dn-flags-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 4px;
}

.dn-total-box {
  flex: 0 0 auto;
}

.status-banner {
  margin: 4px 4px 6px;
  padding: 4px 8px;
  border: 1px solid #1d1d1b;
  text-align: center;
  line-height: 1.05;
  background: #fff;
}

.status-banner--exchange {
  font-size: 20px;
}

.status-banner--exchange strong {
  color: #1565C0;
}

.status-banner--fragile {
  font-size: 40px;
  text-transform: uppercase;
}

.status-banner--fragile strong {
  color: #bc2b18;
  font-weight: 900;
  letter-spacing: 3px;
}
</style>

<style lang="scss">
@media print {
  @page {
    size: A4 portrait;
    margin: 5mm !important;
  }

  /* Removed the global * margin/padding reset — it nukes Bootstrap spacing utilities
     (.ml-2, .mt-2, etc.) and causes text to overlap on iOS Safari.
     Instead, only reset specific layout containers below. */

  html, body {
    height: auto !important;
    width: auto !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  /* body:has() is not supported on iOS Safari 15 and below — removed */

  .pagebreak {
    /* height: 100% removed — depends on parent vh which is 0 on iOS print */
    overflow: visible !important;
    display: flex !important;
    flex-direction: column !important;

    // Handle variable content sizing
    .h4 {
      font-size: 16px !important;
      line-height: 1.2 !important;
      margin: 2px 0 !important;
      padding: 2px !important;
    }

    // Compact exchange and fragile banners
    .text-center.p-1 {
      padding: 2px !important;
      margin: 2px 0 !important;
      font-size: 18px !important;
      line-height: 1 !important;
    }

    // Ensure content fits within the container
    > div {
      flex-shrink: 1 !important;
    }
  }

  .ta {
    white-space: nowrap;
    overflow-x: visible;
    font-size: 22px;
    color: black;
  }

  footer {
    position: fixed;
    bottom: 0;
    width: 100%;
    height: auto; /* Footer height */
  }

  .table thead th {
    border-bottom: 2px solid black;
  }

  body {
    background-color: transparent !important;
  }
  nav.header-navbar {
    display: none;
  }
  .main-menu {
    display: none;
  }
  .header-navbar-shadow {
    display: none !important;
  }
  .content.app-content {
    margin: 0 !important;
    padding: 0 !important;
    width: 100% !important;
  }
  .mycolor {
    background-color: #EA2F2F;
    print-color-adjust: exact;
    -webkit-print-color-adjust: exact;
  }
  .contact {
    background-color: #EA2F2F;
    padding: 20px;
    border-radius: 25px;
    print-color-adjust: exact;
    -webkit-print-color-adjust: exact;
  }

  .dn-after-table-row {
    display: flex !important;
    justify-content: space-between !important;
    align-items: flex-start !important;
    gap: 2mm !important;
    margin: 1mm 3mm !important;
    padding: 0 !important;
    flex: 0 0 auto !important;
  }

  .dn-after-table-left {
    flex: 1 1 auto !important;
    min-width: 0 !important;
  }

  .dn-after-table-right {
    flex: 0 0 auto !important;
    align-self: flex-start !important;
  }

  .dn-price-ttc {
    font-size: 18px !important;
    font-weight: 900 !important;
  }
  footer footer-light footer-static {
    display: inline-block;
  }
  footer.footer {
    display: none;
  }

  .card {
    background-color: transparent;
    box-shadow: none;
  }
  .customizer-toggle {
    display: none !important;
  }

  // Ensure QR/barcode sizing and alignment for print too
  .codes-right {
    margin-top: -12px !important;
    gap: 0 !important;
  }
  .codes-right span {
    font-size: 14px !important;
    font-weight: 800 !important;
    font-family: monospace !important;
    line-height: 1 !important;
  }
  .header-barcode-wrap {
    margin-left: -34px !important;
    transform: translateX(-10px) !important;
  }
  .codes-right {
    gap: 0 !important;
    flex-wrap: nowrap !important;
    align-items: flex-start !important;
  }
  .barcode-small {
    display: block !important;
    overflow: visible !important;

    svg {
      display: block !important;
      width: 390px !important;
      max-width: 390px !important;
      height: 132px !important;
      margin-left: 0 !important;
      overflow: visible !important;
    }
  }
  .barcode-text-fallback {
    font-size: 13px !important;
    font-family: monospace !important;
    letter-spacing: 2px !important;
    text-align: center !important;
    display: block !important;
    color: #000 !important;
    margin-top: 2px !important;
    print-color-adjust: exact !important;
    -webkit-print-color-adjust: exact !important;
  }
  .codes-left {
    gap: 0 !important;
    margin-top: 4px !important;
    margin-bottom: 6px !important;
  }
  .logo-top {
    margin-top: -10px !important;
  }
  .dn-header-logo {
    width: 100px !important;
    max-width: 100px !important;
    height: auto !important;
  }
  .dn-header-right-codes {
    align-items: flex-end !important;
  }
  .dn-header-codes {
    gap: 6px !important;
    margin-top: 4px !important;
  }
  .dn-header-codes .barcode-text-fallback {
    font-size: 10px !important;
    letter-spacing: 1px !important;
  }
  .footer-codes-wrap {
    gap: 0 !important;
    flex-wrap: nowrap !important;
    align-items: flex-end !important;
  }
  .footer-barcode-wrap {
    margin-left: 4px !important;
  }
  .footer-barcode svg {
    display: block !important;
    width: 260px !important;
    max-width: 260px !important;
    height: 110px !important;
    overflow: visible !important;
  }
  .footer-barcode {
    width: 260px !important;
    max-width: 260px !important;
    height: 110px !important;
    overflow: visible !important;
  }
  .company-meta .h4 {
    font-size: 13px !important;
    line-height: 1.15 !important;
  }
  .client-info-box {
    font-size: 21px !important;
    line-height: 1.1 !important;
  }
  .supplier-info-box {
    font-size: 13px !important;
    line-height: 1.1 !important;
  }
  .dn-info-row {
    gap: 6px !important;
  }
  .dn-info-col {
    flex: 1 1 45% !important;
    min-width: 0 !important;
  }
  .status-banner {
    margin: 3px 4px !important;
    padding: 3px 8px !important;
    border: 1px solid #1d1d1b !important;
    text-align: center !important;
    line-height: 1 !important;
    background: #fff !important;
  }
  .status-banner--exchange {
    font-size: 28px !important;
  }
  .status-banner--exchange strong {
    color: #1565C0 !important;
  }
  .status-banner--fragile {
    font-size: 44px !important;
    text-transform: uppercase !important;
  }
  .status-banner--fragile strong {
    color: #bc2b18 !important;
    font-weight: 900 !important;
    letter-spacing: 3px !important;
  }

  // Invoice Specific Styles
  .invoice-preview-wrapper {
    width: 100% !important;
    /* Removed height: 50vh — iOS Safari has no viewport during print so vh=0
       and cards collapse. Use auto height so the content naturally fills the page. */
    margin: 0 !important;
    padding: 0.5mm 0 0 !important;

    .row.invoice-preview {
      width: 100% !important;
      height: 100% !important;
      margin: 0 !important;

      .col-md-8 {
        max-width: 100% !important;
        width: 100% !important;
        height: 100% !important;
        flex-grow: 1;
        padding: 0 !important;
      }

      .invoice-preview-card {
        /* Removed fixed height — it relied on the vh-based parent and collapsed on iOS */
        margin: 0 !important;
        border: none !important;
        overflow: visible !important;

        .card-body:nth-of-type(2) {
          .row {
            > .col-12 {
              max-width: 50% !important;
            }

            .col-12:nth-child(2) {
              display: flex;
              align-items: flex-start;
              justify-content: flex-end;
              margin-top: 0 !important;
            }
          }
        }
      }
    }

    // Action Right Col
    .invoice-actions {
      display: none;
    }
  }
  table,
  td,
  th {
    padding: 10px;
    text-align: left;
    border: 1px solid black;
  }

  table {
    width: 100%;
    border-collapse: collapse;
  }
}

// Source company badge styling
.badge.badge-info {
  background-color: #17a2b8;
  color: white;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 3px;
  font-weight: normal;
}

@media print {
  .badge.badge-info {
    background-color: #17a2b8 !important;
    color: white !important;
    print-color-adjust: exact;
    -webkit-print-color-adjust: exact;
  }
}
</style>
